import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Input, OnInit} from '@angular/core';
import {moveItemInArray} from '@angular/cdk/drag-drop';
import {debounceTime, mergeMap, of, Subject} from 'rxjs';
import {Store} from "@ngrx/store";
import {Compbaser} from "../../../components/compbaser";
import {compareIds, isMimeTypeExists, isSubScene, sceneItemInvalid} from "../../../common";
import {getFontIcons} from "../../../common/get-icons";
import {IAppState} from "../../../store/state/app.state";
import {actionCRUDScene, actionSelectedSceneItemId, actionUpdateSceneItem, actionUpdateSceneItemGroup} from "../../../store/actions/user.actions";
import {Id, ISceneBlock, IResource, IPlayerProps, ISelectedSceneSceneId} from "../../../interfaces";
import {IBlockLabelProp} from "../scene-blocks/block-constants";
import {faBullseye, faEye, faEyeSlash, faFolder, faFolderOpen, faImage, faLock, faLockOpen, faHandPointer} from "@fortawesome/free-solid-svg-icons";
import {ContainerService, StoreSelectorsService} from "../../../services";
import {ToastrService} from 'ngx-toastr';
import {TranslocoService} from '@ngneat/transloco';
import {debounce} from "utils-decorators";
import * as _ from "lodash";

interface SubChange {
    status?: any;
    filter?: any;
}

interface TreeNode {
    filename: string;
    id: Id;
    type: any;
    locked?: boolean;
    mimeType?: string;
    invisible?: boolean;
    isBackground?: boolean;
    icon?: any;
}

@Component({
    selector: 'app-scene-layers',
    templateUrl: './scene-layers.component.html',
    styleUrls: ['./scene-layers.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SceneLayersComponent extends Compbaser implements OnInit, AfterViewInit {
    readonly faLock = faLock;
    readonly faLockOpen = faLockOpen;
    readonly faEye = faEye;
    readonly faEyeSlash = faEyeSlash;
    readonly faFolder = faFolder;
    readonly faFolderOpen = faFolderOpen;
    readonly faBullseye = faBullseye;
    readonly faImage = faImage;
    readonly faHandPointer = faHandPointer;
    private subSelection = new Subject<SubChange>();

    resources: IResource[];
    scenePlayer: IPlayerProps;
    scenesDictionary = {};
    filter = "";
    selectedSceneId = "";
    activeIds = [];
    players = [];
    expandTree1 = true;
    expandTree2 = true;

    rootNode: TreeNode;
    treeSource = {
        'fixed': [],
        'common': []
    };

    filteredSource = {
        'fixed': [],
        'common': []
    };
    isMultiMode = false;
    sceneMime: any;
    loading = false;

    constructor(
        private store: Store<IAppState>,
        private storeSelector: StoreSelectorsService,
        private cd: ChangeDetectorRef,
        private toastr: ToastrService,
        private translocoService: TranslocoService,
        private container: ContainerService
    ) {
        super();
    }

    @HostListener('window:keydown', ['$event'])
    keyDownEvent(event: KeyboardEvent) {
        if (event.shiftKey || event.ctrlKey || event.metaKey) {
            this.isMultiMode = true;
        } else {
            this.isMultiMode = false;
        }
    }

    @HostListener('window:keyup', ['$event'])
    keyUpEvent(event: KeyboardEvent) {
        this.isMultiMode = false;
    }

    ngOnInit(): void {
        this.cod(
            this.storeSelector.listenSelectedScene().subscribe((i_scene: IPlayerProps) => {
                if (!i_scene?.Player) return;
                this.scenePlayer = i_scene;
                if (this.scenePlayer.Player.Data["0"].Scene["0"].Players["0"].Player) {
                    // this.players = _.cloneDeep(this.scenePlayer.Player.Data["0"].Scene["0"].Players["0"].Player);
                    this.players = this.scenePlayer.Player.Data["0"].Scene["0"].Players["0"].Player;
                }

                this.buildTree();
            })
        );
        this.cod(
            this.storeSelector.getResources().subscribe((i_resources) => {
                this.resources = i_resources;
                this.buildTree();
            })
        );

        this.cod(this.container.getActiveSceneItemIds().subscribe((_ids) => {
            if (_ids) {
                this.activeIds = _ids;
            } else {
                this.activeIds = [this.selectedSceneId];
            }

            this.autoScrollItem();
            this.cd.markForCheck();
        }));

        this.cod(
            this.storeSelector.listenSelectedSceneIdAndSceneItemId()
                .pipe(
                    mergeMap((v: ISelectedSceneSceneId) => {
                        /** improve performance, if sceneMime is already set no need to check again **/
                        if (this.sceneMime) {
                            return of(null);
                        } else {
                            return this.storeSelector.getSceneMimeType(v.sceneId);
                        }
                    })
                )
                .subscribe((v) => {
                    if (!v) return;
                    this.sceneMime = v;
                })
        );

        this.cod(
            this.subSelection.pipe(
                debounceTime(100)
            ).subscribe(event => {
                if (!event) return;
                if (event.filter) {
                    this.updateFilter();
                }
            })
        );
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.autoScrollItem();
        }, 500);
    }

    @debounce(333)
    buildTree() {
        if (!this.scenePlayer || !this.resources) return;
        this.selectedSceneId = this.scenePlayer.Player.$.id;

        if (!this.activeIds || !this.activeIds[0]) {
            this.activeIds = [this.selectedSceneId];
        }

        this.rootNode = {
            filename: this.scenePlayer.Player.$.label,
            type: "",
            id: this.scenePlayer.Player.$.id,
            icon: getFontIcons({$: {id: this.storeSelector.blockLabels.BLOCKCODE_SCENE.id}}).icon
        };

        const fixedTreeSource = [];
        const commonTreeSource = [];

        if (this.players && this.players.length > 0) {
            for (const _player of this.players) {
                if (sceneItemInvalid(_player)) return;

                const locked = String(_player.Data[0].Layout?.[0].$.locked) === 'true';
                const invisible = String(_player.Data[0].Layout?.[0].$.invisible) === 'true';
                const isBackground = locked && String(_player.Data[0].Layout?.[0].$.isBackground) === 'true';

                const block: IBlockLabelProp = this.storeSelector.getBlockLabelById(_player.$.player);
                const blockIcon = this.assignIcon(block, block.types, block.jsonItemBased);
                const nestedScene = isSubScene(_player);

                const node: TreeNode = {
                    id: _player.$.id,
                    type: nestedScene ? "+" : "",
                    filename: this.getNodeName(_player),
                    locked,
                    invisible,
                    isBackground,
                    mimeType: _player.$.mimeType || "",
                    icon: blockIcon
                };

                if (isBackground) {
                    fixedTreeSource.push(node);
                } else {
                    commonTreeSource.push(node);
                }
            }
        }
        
        this.treeSource['fixed'] = fixedTreeSource;
        this.treeSource['common'] = commonTreeSource;
        this.updateFilter();

        setTimeout(() => {
            this.loading = false;
        });
        this.cd.markForCheck();
    }

    getNodeName(i_item: ISceneBlock) {
        if (this.storeSelector.isResourceBased(i_item.$.player)) {
            const res = this.resources.find((r) => {
                return compareIds(r.resource_id, i_item.Data["0"].Resource["0"].$.resource);
            });
            if (res) {
                return res.resource_name;
            } else {
                return i_item.$.label;
            }
        } else {
            return i_item.$.label;
        }
    }

    onItemClick(node: TreeNode, event) {
        if (node.invisible || node.locked) {
            return;
        }
        if (!this.activeIds) this.activeIds = [];

        if (node.id == this.selectedSceneId || !this.isMultiMode) {
            this.activeIds = [node.id];
        } else {
            const ind = this.activeIds.indexOf(node.id);

            // if it is selected, deselect, if else, select
            if (ind == -1) {
                this.activeIds.push(node.id);
            } else {
                this.activeIds.splice(ind, 1);
            }

            // remove scene id if it is selected
            if (this.activeIds.indexOf(this.selectedSceneId) > -1) {
                this.activeIds.splice(this.activeIds.indexOf(this.selectedSceneId), 1);
            }
        }

        this.container.activeSceneItemIds.next(this.activeIds);

        if (this.activeIds.length == 1) {
            this.store.dispatch(actionSelectedSceneItemId({id: this.activeIds[0]}));
        } else {
            this.store.dispatch(actionSelectedSceneItemId({id: this.selectedSceneId}));
        }

        this.cd.markForCheck();
        event.stopImmediatePropagation();
        event.preventDefault();
    }

    private autoScrollItem() {
        if (this.activeIds && this.activeIds.length > 0) {
            const activeItem = document.getElementById(`layer_${this.activeIds[0]}`);
            if (activeItem) {
                activeItem.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "nearest"
                });
                this.cd.markForCheck();
            }
        }
    }

    private assignIcon(block, sceneItemMimeType, isBlockItemBased) {
        if (sceneItemMimeType && isBlockItemBased && sceneItemMimeType?.length > 0 && isMimeTypeExists(sceneItemMimeType, this.sceneMime)) {
            return getFontIcons({$: {id: this.sceneMime}}).icon;
        }
        return block.icon;
    }

    /** Toggle scene items between visible or ininvisible */
    toggleInvisibleItem(e, status: boolean, node: TreeNode = null) {
        this.updateItemStatus('invisible', status, node);
        e.stopImmediatePropagation();
        e.preventDefault();
    }

    /** Toggle scene items between locked or unlocked */
    toggleLockedItem(e, status: boolean, node: TreeNode = null) {
        this.updateItemStatus('locked', status, node);

        // this.storeSelector.dispatch(actionUpdateSceneItem({
        //     sceneId: this.selectedSceneId,
        //     sceneItemId: node.id,
        //     values: [{
        //         key: 'locked',
        //         value: status,
        //         storePath: 'Data["0"].Layout["0"].$',
        //         cloneValue: false
        //     }]
        // }));
        // this.storeSelector.dispatch({type: 'BREAK_MERGE'});
        // this.storeSelector.dispatch(actionSelectedSceneItemId({id: this.selectedSceneId}));
        // this.container.activeSceneItemIds.next([this.selectedSceneId]);

        e.stopImmediatePropagation();
        e.preventDefault();
    }

    private updateItemStatus(key: 'locked' | 'invisible', status: boolean, node: TreeNode = null) {
        let nodeIds = [];
        if (!node) {
            nodeIds = this.activeIds;
        } else {
            nodeIds = [node.id];
        }

        if (nodeIds.length == 1 && nodeIds[0] == this.selectedSceneId) {
            return;
        }

        const values: any = [{
            key,
            value: status ? 'true' : 'false',
            storePath: 'Data["0"].Layout["0"].$',
            cloneValue: false
        }];

        if (node && node.isBackground) {
            values.push({
                key: 'isBackground',
                value: 'false',
                storePath: 'Data["0"].Layout["0"].$',
                cloneValue: false
            });
        }

        this.storeSelector.dispatch(actionUpdateSceneItemGroup({
            items: nodeIds.map((_id) => ({
                sceneId: this.selectedSceneId,
                sceneItemId: _id,
                values
            }))
        }));

        this.storeSelector.dispatch(actionSelectedSceneItemId({id: this.selectedSceneId}));
        this.container.activeSceneItemIds.next([this.selectedSceneId]);
    }

    /** drop event of scene-tree */
    dropItem(event) {
        // ignore drops outside of the tree
        if (this.loading) return;
        if (!event.isPointerOverContainer) return;
        if (event.previousContainer !== event.container) return;

        if (event.item.data && event.item.data.locked) {
            this.toastr.warning(this.translocoService.translate('toast.disableDrag', {}, this.translocoService.getActiveLang()));
            return;
        }

        if (event.item.data && event.item.data.invisible) {
            this.toastr.warning(this.translocoService.translate('toast.disableDrag', {}, this.translocoService.getActiveLang()));
            return;
        }

        // deep clone the data source so we can mutate it
        // const cloneTreeData = JSON.parse(JSON.stringify(this.flatDataSource.data));
        const prevIndex = event.previousIndex;
        const currIndex = event.currentIndex;
        if (currIndex < 0 || prevIndex < 0) {
            return;
        }

        // const dropId = event.item.data.id;
        // let swappingIds = [];
        // // let upper;
        // if (prevIndex < currIndex) {
        //     swappingIds = items.slice(prevIndex + 1, currIndex + 1).map((_item) => _item.id);
        //     // upper = false;
        // } else if (prevIndex > currIndex) {
        //     swappingIds = items.slice(currIndex, prevIndex).map((_item) => _item.id);
        //     swappingIds.reverse();
        //     // upper = true;
        // } else {
        //     return;
        // }

        // swappingIds.forEach((_id) => {
        //     const ind1 = this.players.findIndex((_player) => _player.$.id == _id);
        //     const ind2 = this.players.findIndex((_player) => _player.$.id == dropId);
        //     const tmp = this.players[ind1];
        //     this.players[ind1] = this.players[ind2];
        //     this.players[ind2] = tmp;

        //     // this.swapSceneItems(dropId, _id);
        //     // this.container.updateSwappingIds([dropId, _id]);
        // });

        this.loading = true;

        const items = event.container.data;
        const players = _.cloneDeep(this.players);
        const prevPlayerIndex = players.findIndex((_player) => compareIds(_player.$.id, event.item.data.id));
        const nextPlayerIndex = players.findIndex((_player) => compareIds(_player.$.id, items[currIndex].id));

        moveItemInArray(players, prevPlayerIndex, nextPlayerIndex);
        moveItemInArray(items, prevIndex, currIndex);

        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.selectedSceneId,
            sceneItemId: this.selectedSceneId,
            values: [{
                key: 'Player',
                value: players,
                storePath: 'Data["0"].Scene["0"].Players["0"]',
                cloneValue: false
            }],
            disableCRUD: true
        }));

        this.storeSelector.dispatch(actionCRUDScene({
            data: {
                command: 'reorder',
                sceneId: this.selectedSceneId,
                itemIds: []
            }
        }));
        this.storeSelector.dispatch({type: 'BREAK_MERGE'});
        this.cd.markForCheck();
    }

    // fire when change search of input
    @debounce(300)
    changeSearchKey() {
        this.subSelection.next({filter: true});
    }

    updateFilter() {
        if (this.filter) {
            const _filter = this.filter.toLowerCase();
            Object.keys(this.treeSource).forEach((key) => {
                if (!this.treeSource[key]) return;
                this.filteredSource[key] = this.treeSource[key].filter((v: TreeNode) => v.filename.toLowerCase().indexOf(_filter) > -1);
            });
        } else {
            this.filteredSource = _.cloneDeep(this.treeSource);
        }
        this.cd.markForCheck();
    }

    // clear search key by clicking "close" icon
    clearSearchKey() {
        this.filter = '';
        this.updateFilter();
    }
}
